import { useEffect, useState } from "react";
import CarrolltonHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Carrollton?", a: "We offer Carrollton-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes." },
  { q: "Do I need to visit an office in Carrollton?", a: "No. Our service is remote: you complete forms online, visit a local acceptance facility (like a Carrollton post office or Dallas/Denton County clerk) for in-person verification if needed, then ship documents to us via overnight courier." },
  { q: "Where is the nearest passport acceptance facility in Carrollton?", a: "Carrollton has acceptance facilities at the Carrollton Main Post Office, Addison post offices, and the Dallas County and Denton County clerk offices in nearby cities. We provide a curated list of Carrollton-area facilities with address, phone, and photo availability." },
  { q: "Is DFW Airport close to Carrollton?", a: "Yes — DFW International Airport is approximately 10 miles east of Carrollton, making it one of the closest major cities to the DFW hub. Carrollton residents departing internationally through DFW are particularly well-positioned for our courier service, which can return passports within 24–48 hours for emergency needs." },
  { q: "How much does expedited passport service cost in Carrollton?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. We offer a best price guarantee and rates typically 30–100% lower than third-party resellers." },
  { q: "What documents do Carrollton applicants need?", a: "For a new adult passport: proof of citizenship, government-issued photo ID, DS-11 form, and passport photo. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents or notarized consent, birth certificate, and photos. We provide a Carrollton-specific checklist." },
  { q: "Can I get a passport photo in Carrollton?", a: "Yes. Many Carrollton CVS, Walgreens, and UPS Store locations offer passport photos. We recommend confirming State Department photo standard compliance before visiting. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
  { q: "How do I track my Carrollton passport application?", a: "Once we submit your application, we provide tracking at every stage. You can also check the State Department's online passport status system. Processing from Carrollton to final return shipping typically matches your chosen speed tier." },
  { q: "What are the main reasons Carrollton residents need expedited passports?", a: "Carrollton's diverse tech-sector workforce generates many last-minute corporate travel needs. The city's Korean-American, Indian-American, and Taiwanese-American communities book family trips and heritage visits on short notice. Proximity to DFW means residents are frequent international travelers." },
  { q: "What if my passport is lost or stolen in Carrollton?", a: "File a police report if stolen, then complete Form DS-64 along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. Our expedited service can process the replacement as fast as 24 hours." },
];

function CarrolltonStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function CarrolltonProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Carrollton Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Carrollton-area travelers. Choose the timeline that matches your DFW departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Carrollton travelers departing within 3 days. Includes same-day document review and overnight courier submission to State Department." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For DFW-area business travelers and families with travel in 1–2 weeks. Best value for most Carrollton clients." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Carrollton residents planning ahead. Significantly faster than standard post office processing (10–13 weeks)." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Carrollton travelers with imminent DFW departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Carrollton residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Carrollton adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 guidance for Carrollton residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Carrollton families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport for frequent Carrollton business travelers with overlapping trips." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before State Department submission." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Carrollton Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Complete passport solutions for Carrollton's diverse DFW-area community — from corporate tech workers to international families to university students.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Carrollton</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Carrollton is a diverse, rapidly-growing city straddling Dallas and Denton Counties in the heart of the DFW Metroplex. With a population approaching 140,000, Carrollton is one of the most ethnically diverse cities in North Texas — home to one of the largest Korean-American communities in the southern United States, a significant Vietnamese-American population centered around the "Koreatown" corridor along Belt Line Road, and a fast-growing Indian-American tech worker community.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Carrollton's Korean business district on Belt Line Road is sometimes called "K-Town" or "Little Korea" — a stretch of Korean restaurants, grocery stores, karaoke bars, and businesses that serves the entire DFW Korean-American community. This community has strong ties to South Korea and generates significant international travel demand — family visits for Chuseok and Lunar New Year, business trips to Seoul and Busan, student exchanges with Korean universities, and medical tourism. Passports for this community need to be current and valid, often requiring renewal every five years for heavy travelers.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The city's tech sector is substantial. Carrollton hosts numerous technology companies, engineering firms, and logistics businesses that send employees internationally on short notice. The corridor between Carrollton and Addison — just south on the Dallas County line — is particularly dense with corporate offices that generate steady international business travel demand.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            DFW International Airport is approximately 10 miles east of central Carrollton — an exceptionally short commute compared to most DFW suburbs. This proximity means Carrollton residents are frequent DFW users and face passport urgencies regularly. A corporate assignment that surfaces on Thursday for a Monday departure through DFW creates a weekend passport crisis that our courier service handles better than any alternative.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Carrollton is also home to a significant number of Taiwanese-American families who maintain close ties to Taiwan, China, and Southeast Asia. Many of these residents hold dual citizenship or travel on multiple passports, creating complex documentation needs that require careful handling. We understand these community-specific travel patterns and can guide Carrollton families through the appropriate process for their situation.
          </p>
        </div>
      </div>
    </section>
  );
}

function CarrolltonTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Carrollton Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DFW — Dallas-Fort Worth International Airport</h3>
                <p className="text-gray-400 text-xs">~10 miles from Carrollton, American Airlines hub, 230+ international destinations</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Carrollton is one of the closest major suburbs to DFW International Airport. The short 10-mile drive means Carrollton residents are among the most frequent DFW international travelers in the Metroplex. American Airlines' massive hub network connects Carrollton to Seoul, Tokyo, Taipei, Bangalore, London, and virtually every other major international market. Proximity to DFW also means passport emergencies discovered the night before a departure are more common here than in more distant suburbs.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Carrollton's proximity to DFW is a double-edged sword — the airport is so convenient that residents book international travel with very short timelines. Always check passport validity before you book, not when you pack.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-community-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Korean-American Community Travel</h3>
                <p className="text-gray-400 text-xs">One of the largest Korean-American communities in the South</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Carrollton's Korean-American community books flights to Seoul year-round — for Chuseok, Lunar New Year, family weddings, and business meetings. Korean children born in the U.S. often hold dual citizenship and need to renew both U.S. and Korean passports on overlapping timelines. Korean-American students from Carrollton attend Korean universities for one or two-semester exchange programs that require passports well in advance of orientation.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Carrollton Korean-American families traveling to South Korea should verify that children's U.S. passports meet the 6-month validity requirement well before booking to avoid complications at Seoul Incheon Airport.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-computer-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Tech Sector Corporate Travel</h3>
                <p className="text-gray-400 text-xs">Carrollton-Addison tech corridor, DFW corporate campus belt</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">The Carrollton-Addison corridor is dense with technology companies, engineering firms, data centers, and corporate offices. Tech employees in Carrollton are regularly deployed to India, Southeast Asia, and Europe for project work, client meetings, and training programs. These assignments are often communicated 5–15 days before departure, creating exactly the kind of urgent passport processing need that our courier service is designed to handle.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> If your company sends you on international assignments, keep your passport current at all times. Renew when there are still 2 years of validity — not when the passport expires.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Galveston Cruise Departures</h3>
                <p className="text-gray-400 text-xs">Port of Galveston, Western Caribbean, and Mexico sailings</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Carrollton families driving to Galveston for Royal Caribbean, Carnival, and Disney cruises represent a significant passport urgency market. Many Carrollton families book cruises with 3–6 weeks of lead time, discovering expired passports or children who have never had them. The DFW suburb-to-Galveston cruise pattern is one of the most common passport scenarios we handle for North Texas families.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Even closed-loop Western Caribbean cruises strongly recommend passports. Disney Cruise Line requires them for all passengers. Verify your cruise line's policy before booking.</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-global-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Carrollton's International Community &amp; Heritage Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">Carrollton is one of the most ethnically diverse cities in Texas, with large Korean-American, Indian-American, Vietnamese-American, and Taiwanese-American communities. Heritage travel to South Korea, India, Vietnam, and Taiwan generates consistent passport urgency — especially for children who have never traveled to family home countries before.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function CarrolltonDestinationsSection() {
  const destinations = [
    { name: "South Korea", context: "Heritage visits, Chuseok/Lunar New Year family trips, business travel, exchange student programs", urgency: "Holiday bookings peak 4–6 weeks before Chuseok and Lunar New Year", icon: "ri-map-pin-2-line" },
    { name: "India", context: "Carrollton's Indian-American tech worker community, wedding season travel, family visits to Hyderabad and Bangalore", urgency: "Wedding season travel (Oct–Feb) and holiday visits peak 4–6 weeks out", icon: "ri-map-pin-2-line" },
    { name: "Taiwan & China", context: "Taiwanese-American family visits, business travel, manufacturing supplier meetings", urgency: "Lunar New Year bookings fill up 6–8 weeks in advance", icon: "ri-map-pin-2-line" },
    { name: "Vietnam", context: "Carrollton Vietnamese-American community, Tet holiday family visits, ancestry heritage travel", urgency: "Tet travel peaks January–February with 4–6 week booking windows", icon: "ri-map-pin-2-line" },
    { name: "Mexico & Caribbean", context: "Galveston cruise departures, Cancun resort vacations, family heritage trips to Mexico", urgency: "Spring break and summer cruise bookings peak January through March", icon: "ri-map-pin-2-line" },
    { name: "Japan & Southeast Asia", context: "DFW-to-Tokyo direct flight, tech industry travel, anime and cultural tourism for Carrollton youth", urgency: "Tech deployment assignments often arrive 5–15 days before departure", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "38%", label: "of Carrollton expedited requests are Asian-American heritage and community travel" },
    { stat: "2–4 Weeks", label: "average lead time Carrollton travelers have from booking to international departure" },
    { stat: "Jan–Feb", label: "peak season when Lunar New Year, Tet, and Korean heritage travel create passport urgencies" },
    { stat: "10 Miles", label: "Carrollton's distance from DFW — the closest major DFW suburb to the international terminal" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Carrollton Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across the DFW Metroplex and all of Texas with the same fast, reliable passport processing.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/plano" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Plano <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/irving" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Irving <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CarrolltonTestimonialsSection() {
  const testimonials = [
    { name: "Min-Jun K.", location: "Belt Line, Carrollton", text: "I had a business trip to Seoul in 9 days and my passport expired. Fast Passport Center processed it in 48 hours. They understood the Korea travel requirements perfectly.", rating: 5 },
    { name: "The Patel Family", location: "Carrollton", text: "We booked a trip to India for my parents' anniversary and realized my wife's passport had only 2 months left. They processed the renewal in 5 days. Perfect timing.", rating: 5 },
    { name: "Sarah L.", location: "Addison/Carrollton", text: "My company sent me to London in 12 days and my passport was expired. They handled everything — document review, submission, return shipping. I never had to leave my desk.", rating: 5 },
    { name: "The Nguyen Family", location: "Carrollton", text: "We needed passports for our whole family for a Vietnam trip. They handled all four applications simultaneously and everything came back in 7 days. Incredible service.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Carrollton Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Carrollton Main Post Office", address: "1700 Keller Springs Rd, Carrollton, TX 75006", phone: "(972) 242-4440", photo: "Yes", appointment: "Required" },
    { name: "Carrollton South Station", address: "2626 Old Denton Rd, Carrollton, TX 75007", phone: "(972) 245-0901", photo: "Yes", appointment: "Walk-in" },
    { name: "Addison Post Office", address: "4475 Belt Line Rd, Addison, TX 75001", phone: "(972) 991-6101", photo: "Yes", appointment: "Required" },
    { name: "Farmers Branch Post Office", address: "13755 Gamma Rd, Farmers Branch, TX 75244", phone: "(972) 484-8511", photo: "Yes", appointment: "Walk-in" },
    { name: "Dallas County Clerk - Northwest", address: "5473 Blair Rd, Dallas, TX 75231", phone: "(214) 653-7700", photo: "No", appointment: "Required" },
    { name: "Coppell Post Office", address: "503 W Bethel Rd, Coppell, TX 75019", phone: "(972) 462-9009", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Carrollton Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility. Here are Carrollton-area locations with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Call ahead to confirm availability. We can help find the earliest available slot across all Carrollton-area facilities.</p>
        </div>
      </div>
    </section>
  );
}

function CarrolltonComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Carrollton Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies" },
                { feature: "Carrollton-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CarrolltonMistakesSection() {
  const mistakes = [
    { title: "Assuming DFW Proximity Means Easy Agency Access", desc: "While DFW Airport is close, the Dallas Passport Agency requires appointments for emergencies only. Being close to DFW does not help if you do not have an agency appointment." },
    { title: "Forgetting the 6-Month Rule for Asia", desc: "Carrollton travelers heading to South Korea, Japan, Vietnam, or India often do not realize these countries require 6 months of passport validity beyond the return date. We flag this before submission." },
    { title: "Multiple Passport Complexity", desc: "Many Carrollton Korean-American and Taiwanese-American families hold dual citizenship. Using the wrong passport at check-in or failing to have the correct document for entry can cause serious delays." },
    { title: "Photo Standards Violations", desc: "Korean-style passport photos (common in Carrollton) do not always meet U.S. State Department standards. The backgrounds, sizing, and expression requirements differ. We verify before submission." },
    { title: "Not Starting Early Enough for Minors", desc: "Carrollton families with children who have never had passports often start too late. First-time applications require in-person facility visits with both parents — scheduling this can add a week." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Carrollton Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Carrollton-area acceptance facility. We find the nearest one with the earliest appointment." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed package overnight to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. Full tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Delivered to your Carrollton address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Carrollton Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Carrollton Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Carrollton Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "U.S. State Department", desc: "Official passport information, forms, and processing status updates.", href: "https://travel.state.gov/content/travel/en/passports.html", icon: "ri-government-line" },
            { title: "Travel Advisories", desc: "Current safety and entry requirements for every country.", href: "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html", icon: "ri-earth-line" },
            { title: "Visa Waiver Program", desc: "Check if your destination participates in the ESTA visa waiver program.", href: "https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program", icon: "ri-visa-line" },
          ].map((r, i) => (
            <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarrolltonFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Carrollton Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are flying from DFW in 3 days or sailing from Galveston in 3 weeks, we will get your passport processed on time. A+ BBB rated. Registered State Dept. courier.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function CarrolltonTXPage() {
  useEffect(() => {
    document.title = "Emergency Passport Carrollton, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/carrollton-tx/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Carrollton, TX — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Carrollton's Korean-American community, tech sector workers, DFW business travelers, and North Texas families.");
    setMeta("geo.placename", "Carrollton, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "32.9537;-96.8903");
    setMeta("ICBM", "32.9537, -96.8903");
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <CarrolltonHeroSection />
      <CarrolltonStickyNav />
      <CarrolltonProcessingTimeSection />
      <CarrolltonServicesSection />
      <CarrolltonLocalAreaSection />
      <CarrolltonTravelTimingSection />
      <CarrolltonDestinationsSection />
      <CarrolltonTestimonialsSection />
      <CarrolltonAcceptanceFacilitiesSection />
      <CarrolltonComparisonSection />
      <CarrolltonMistakesSection />
      <CarrolltonHowItWorksSection />
      <CarrolltonFAQSection />
      <CarrolltonHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/carrollton-tx" state="TX" />
      <CarrolltonFinalCTA />
    </div>
  );
}